<!--
  SidebarMenuItem — single source of truth for sidebar row styling.

  Every cauldron sidebar (Informational, Executive, Personalisation,
  Favourites, Platform) uses this primitive for their entries so the
  visual language stays identical across cauldrons — the user only ever
  sees one shape of nav row, in different lists.

  Active-state matching:
    - `activeMatch="exact"` (default): active when `route.path === to`.
    - `activeMatch="prefix"`: active when `route.path.startsWith(to)`. Used
      by the parent rows (e.g. /tools matches /tools/bidding-strategy).
    - `activeMatch="includes"`: active when `route.path.includes(matchKey)`.
      Used by the developer routes (`/catalog`, `/documentation`) that have
      both authenticated and anonymous variants.

  Size variants mirror the existing markup in Sidebar.vue:
    - `size="md"` (default) — the main entries (py-2, text-[13px]).
    - `size="sm"` — the favourites strip (py-1.5, text-[12.5px], smaller icon).
    - `size="child"` — sub-items inside an expanded group (px-2.5 indent).
-->
<template>
  <router-link
    :to="to"
    @click="emit('click')"
    :class="rowClasses"
    :title="rowTitle"
    :aria-label="ariaLabel || label"
  >
    <span v-if="icon" class="relative inline-flex shrink-0">
      <icon
        :path="icon"
        :size="iconSize"
        :class="iconClasses"
      />
      <span
        v-if="stage"
        aria-hidden="true"
        :class="[
          'absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full ring-2 ring-theme-surface dark:ring-theme-bg',
          stage === 'beta' ? 'bg-theme-info' : 'bg-theme-warning',
        ]"
      />
    </span>
    <span v-if="!collapsed" :class="labelClasses">
      {{ label }}
    </span>
  </router-link>
</template>

<script setup>
  /* eslint-disable no-undef */
  import Icon from '@/components/Icon';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRoute, useRouter } from 'vue-router';

  const props = defineProps({
    to: { type: [String, Object], required: true },
    label: { type: String, required: true },
    icon: { type: String, default: '' },
    title: { type: String, default: '' },
    ariaLabel: { type: String, default: '' },
    collapsed: { type: Boolean, default: false },
    /** How the active state matches `route.path` against `to`. */
    activeMatch: {
      type: String,
      default: 'exact',
      validator: (v) => ['exact', 'prefix', 'includes'].includes(v),
    },
    /** Override the string used for `includes` matches (e.g. '/catalog'). */
    matchKey: { type: String, default: '' },
    /** `md` (main rows), `sm` (favourites), `child` (indented sub-items). */
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['md', 'sm', 'child'].includes(v),
    },
    /** Icon color when the row is NOT active. Used by the amber-tinted
     *  favourite-star icons; defaults to the standard gray. */
    inactiveIconClass: { type: String, default: '' },
    /** 'alpha' | 'beta'. Renders a small status dot on the icon (yellow/orange
     *  for alpha, blue for beta) — the sidebar-row equivalent of ToolStageBadge,
     *  for rows too narrow to fit a full chip. */
    stage: { type: String, default: '' },
  });

  const emit = defineEmits(['click']);
  const route = useRoute() || {};
  const router = useRouter();
  const { t, te } = useI18n();

  // Native hover title carries the stage in words too, since the dot itself
  // is decorative (aria-hidden) and collapsed rows have no visible label.
  const rowTitle = computed(() => {
    const base = props.title || props.label;
    const stageKey = `tool_stage.${props.stage}.label`;
    if (props.stage && te(stageKey)) return `${base} · ${t(stageKey)}`;
    return base;
  });

  // Resolve the link's URL path for active-state comparison. `to` can be
  // a string path OR a Vue Router location object. Named-route objects
  // (`{ name: 'tenants.index' }`) don't carry a `path` field, so we
  // resolve them via the router — otherwise the `prefix` active-match
  // would compare against an empty string and match EVERY route.
  const toPath = computed(() => {
    if (typeof props.to === 'string') return props.to;
    if (props.to?.path) return props.to.path;
    if (router && props.to) {
      try {
        const resolved = router.resolve(props.to);
        return resolved?.path || '';
      } catch (_e) {
        return '';
      }
    }
    return '';
  });

  const isActive = computed(() => {
    const current = route.path || '';
    const target = toPath.value;
    // Empty target shouldn't match anything — without this guard, a
    // `prefix` check against `''` would treat every route as a match.
    if (!target) return false;
    if (props.activeMatch === 'prefix') {
      return current === target || current.startsWith(`${target}/`);
    }
    if (props.activeMatch === 'includes') {
      const key = props.matchKey || target;
      return key && current.includes(key);
    }
    return current === target;
  });

  const iconSize = computed(() => (props.size === 'sm' ? 16 : 18));

  const rowClasses = computed(() => {
    const base = [
      'flex items-center rounded-lg transition-colors duration-150 mb-0.5 focus:outline-none focus-visible:shadow-theme-focus',
      props.collapsed ? 'justify-center px-0' : 'gap-2.5 px-2.5',
    ];
    if (props.size === 'sm') base.push('py-1.5');
    else base.push('py-2');
    if (props.size === 'child') base.push('ml-2');

    if (isActive.value) {
      base.push('text-theme-palette-emerald dark:text-theme-palette-emerald');
    } else if (props.size === 'sm') {
      base.push(
        'text-theme-text-secondary hover:bg-theme-bg-subtle dark:hover:bg-theme-bg/50 hover:text-theme-text dark:hover:text-theme-text-muted',
      );
    } else {
      base.push(
        'text-theme-text-secondary hover:bg-theme-bg-subtle dark:hover:bg-theme-bg/50',
      );
    }
    return base;
  });

  const iconClasses = computed(() => {
    if (isActive.value) return 'text-theme-palette-emerald';
    if (props.inactiveIconClass) return `${props.inactiveIconClass} shrink-0`;
    return 'text-theme-text-muted';
  });

  const labelClasses = computed(() => {
    const sizing = props.size === 'sm' ? 'text-[12.5px] truncate min-w-0 flex-1' : 'text-[13px] font-medium';
    return sizing;
  });
</script>
